import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import BlogCTA from '../components/BlogCTA.jsx';
import { setPageSEO } from '../utils/seo.js';
import { getPublishedBlogs, slugify } from '../utils/blogApi.js';
import './Blog.css';

function formatDate(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
}

function normalizeCategory(value) {
  const category = slugify(value);

  return category !== 'news' && category.endsWith('s') ? category.slice(0, -1) : category;
}

function Blog() {
  const { contentType = 'blog' } = useParams();
  const [blogs, setBlogs] = useState([]);
  const [activeTag, setActiveTag] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const normalizedContentType = slugify(contentType);
  const contentTypeTitle = normalizedContentType
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

  useEffect(() => setPageSEO({
    title: `${contentTypeTitle} - Clientalio`,
    description: `Read Clientalio ${contentTypeTitle.toLowerCase()} about testimonials, social proof, customer stories, and growing trust with authentic feedback.`,
    canonical: `https://clientalio.com/${normalizedContentType}`,
    ogTitle: `${contentTypeTitle} - Clientalio`,
    ogDescription: `Explore the latest Clientalio ${contentTypeTitle.toLowerCase()}.`,
    twitterTitle: `${contentTypeTitle} - Clientalio`,
    twitterDescription: `Explore the latest Clientalio ${contentTypeTitle.toLowerCase()}.`
  }), [contentTypeTitle, normalizedContentType]);

  useEffect(() => {
    setActiveTag('All');
  }, [normalizedContentType]);

  useEffect(() => {
    let isMounted = true;

    async function loadBlogContent() {
      setIsLoading(true);
      setError('');

      try {
        const blogItems = await getPublishedBlogs();

        if (!isMounted) {
          return;
        }

        setBlogs(blogItems);
      } catch (requestError) {
        if (isMounted) {
          setError('We could not load blog posts right now. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadBlogContent();

    return () => {
      isMounted = false;
    };
  }, []);

  const categoryBlogs = useMemo(() => blogs.filter((blog) => {
    const blogContentType = blog.contentTypeSlug || slugify(blog.contentType || blog.category);

    return normalizeCategory(blogContentType) === normalizeCategory(normalizedContentType);
  }), [blogs, normalizedContentType]);

  const tags = useMemo(() => (
    Array.from(new Set(categoryBlogs.flatMap((blog) => blog.tags).filter(Boolean)))
  ), [categoryBlogs]);

  const filteredBlogs = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    return categoryBlogs.filter((blog) => {
      const matchesSearch = !normalizedSearch || [blog.title, blog.excerpt, blog.contentType, blog.category, blog.tags.join(' ')]
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch);
      const matchesTag = activeTag === 'All' || blog.tags.includes(activeTag);

      return matchesSearch && matchesTag;
    });
  }, [activeTag, categoryBlogs, searchQuery]);

  return (
    <main className="blog-page">
      <section className="blog-hero">
        <div className="container blog-hero-inner">
          <p className="blog-eyebrow">Clientalio {contentTypeTitle}</p>
          <h1>Practical ideas for collecting and showcasing customer proof.</h1>
          <p>
            Explore product updates, testimonial strategy, and customer trust guides from Clientalio.
          </p>
        </div>
      </section>

      <section className="blog-content">
        <div className="container blog-content-inner">
          <div className="blog-toolbar">
            <div className="blog-search">
              <label htmlFor="blog-search-input">Search blog posts</label>
              <input
                id="blog-search-input"
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search articles..."
              />
            </div>

            <div className="blog-filters" aria-label="Blog filters">
              {tags.length > 0 && (
                <select value={activeTag} onChange={(event) => setActiveTag(event.target.value)}>
                  <option value="All">All tags</option>
                  {tags.map((tag) => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {isLoading && <p className="blog-status">Loading blog posts...</p>}
          {!isLoading && error && <p className="blog-status blog-status-error">{error}</p>}
          {!isLoading && !error && filteredBlogs.length === 0 && (
            <p className="blog-status">No blog posts matched your filters.</p>
          )}

          {!isLoading && !error && filteredBlogs.length > 0 && (
            <div className="blog-grid">
              {filteredBlogs.map((blog) => (
                <article className="blog-card" key={blog.id || blog.slug}>
                  {blog.imageUrl && <img src={blog.imageUrl} alt="" className="blog-card-image" loading="lazy" />}
                  <div className="blog-card-body">
                    <div className="blog-meta">
                      {(blog.contentType || blog.category) && <span>{blog.contentType || blog.category}</span>}
                      {formatDate(blog.publishedAt) && <time dateTime={blog.publishedAt}>{formatDate(blog.publishedAt)}</time>}
                    </div>
                    <h2>{blog.title}</h2>
                    {blog.excerpt && <p>{blog.excerpt}</p>}
                    {blog.tags.length > 0 && (
                      <div className="blog-tags">
                        {blog.tags.slice(0, 3).map((tag) => <span key={tag}>{tag}</span>)}
                      </div>
                    )}
                    <Link to={`/${blog.contentTypeSlug || slugify(blog.contentType || blog.category)}/${blog.slug}`} className="blog-read-link">Read article</Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <BlogCTA location="blog_listing_cta" />
    </main>
  );
}

export default Blog;